/**
 * DynamicSpecification Value Object
 * 
 * Represents a single equipment specification with intelligent formatting.
 * Handles dynamic spec_keys and provides type-safe value extraction.
 */

/**
 * Database specification record (from equipment_specifications_v2)
 */
export interface DatabaseSpecificationRecord {
  spec_key: string
  value_text?: string | null
  value_numeric?: number | null
  value_integer?: number | null
  value_date?: string | null
  value_boolean?: boolean | null
  value_reference_id?: string | null
  template?: {
    label_en?: string
    label_no?: string
    data_type?: string
    reference_table?: string
  } | null
  unit?: {
    symbol?: string
  } | null
}

/**
 * Specification template metadata
 */
export interface SpecificationTemplate {
  spec_key: string
  label_no: string
  label_en: string
  data_type: 'text' | 'numeric' | 'integer' | 'date' | 'boolean' | 'reference'
  unit_id?: string | null
  display_order?: number | null
  reference_table?: string | null
}

/**
 * Value object representing a single specification with formatting logic
 */
export class DynamicSpecification {
  public readonly key: string
  public readonly value: string
  public readonly label: string
  public readonly dataType: string
  public readonly unit?: string
  public readonly priority?: number
  public readonly referenceTable?: string

  private constructor(
    key: string,
    value: string,
    label: string,
    dataType: string,
    unit?: string,
    priority?: number,
    referenceTable?: string
  ) {
    this.key = key
    this.value = value
    this.label = label
    this.dataType = dataType
    this.unit = unit
    this.priority = priority
    this.referenceTable = referenceTable
  }

  /**
   * Create specification from database record
   * Returns null if specification has no value
   */
  static fromDatabase(
    dbSpec: DatabaseSpecificationRecord,
    template?: SpecificationTemplate
  ): DynamicSpecification | null {
    // Extract actual value from the correct column
    const value = this.extractValue(dbSpec)
    
    // Skip if no value
    if (!value || value.trim() === '') {
      return null
    }

    // Create bilingual label (Norwegian/English)
    const labelNo = template?.label_no || (dbSpec.template as any)?.label_no
    const labelEn = template?.label_en || (dbSpec.template as any)?.label_en
    
    let label: string
    if (labelNo && labelEn && labelNo !== labelEn) {
      // Both labels exist and are different - combine them
      label = `${labelNo}/${labelEn}`
    } else {
      // Use whichever is available, or generate from spec_key
      label = labelNo || labelEn || this.generateLabel(dbSpec.spec_key)
    }
    
    const dataType = template?.data_type || dbSpec.template?.data_type || 'text'
    const unit = dbSpec.unit?.symbol
    // Priority from template cache OR embedded template (for synthetic specs)
    const priority = template?.display_order ?? (dbSpec.template as any)?.display_order ?? undefined
    const referenceTable = template?.reference_table || dbSpec.template?.reference_table

    return new DynamicSpecification(
      dbSpec.spec_key,
      value,
      label,
      dataType,
      unit,
      priority,
      referenceTable
    )
  }

  /**
   * Extract value from database record based on data type columns
   */
  private static extractValue(dbSpec: DatabaseSpecificationRecord): string {
    if (dbSpec.value_text) return dbSpec.value_text
    if (dbSpec.value_numeric !== null && dbSpec.value_numeric !== undefined) {
      return dbSpec.value_numeric.toString()
    }
    if (dbSpec.value_integer !== null && dbSpec.value_integer !== undefined) {
      return dbSpec.value_integer.toString()
    }
    if (dbSpec.value_date) return dbSpec.value_date
    if (dbSpec.value_boolean !== null && dbSpec.value_boolean !== undefined) {
      // Convert boolean to Yes/No instead of true/false for control cards
      return dbSpec.value_boolean ? 'Yes' : 'No'
    }
    if (dbSpec.value_reference_id) return dbSpec.value_reference_id
    return ''
  }

  /**
   * Generate fallback label from spec_key if template not found
   */
  private static generateLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  /**
   * Check if specification has a non-empty value
   */
  hasValue(): boolean {
    return !!this.value && this.value.trim() !== ''
  }

  /**
   * Check if value looks like a date
   */
  isDateValue(): boolean {
    return (
      this.dataType === 'date' ||
      /^\d{4}-\d{2}-\d{2}/.test(this.value) ||
      /^\d{2}\.\d{2}\.\d{4}/.test(this.value) ||
      /^\d{2}\/\d{2}\/\d{4}/.test(this.value)
    )
  }

  /**
   * Check if this is a weight specification
   */
  isWeightSpec(): boolean {
    const lowerKey = this.key.toLowerCase()
    return (
      lowerKey === 'mgw' ||
      lowerKey === 'wll' ||
      lowerKey === 'swl' ||
      lowerKey === 'tare' ||
      lowerKey === 'payload' ||
      lowerKey === 'capacity' ||
      lowerKey.includes('weight')
    )
  }

  /**
   * Check if this is an angle specification
   */
  isAngleSpec(): boolean {
    const lowerKey = this.key.toLowerCase()
    return (
      lowerKey.includes('angle') ||
      lowerKey.includes('@') ||
      lowerKey.includes('_at') ||
      lowerKey === 'mgw_at' ||
      lowerKey === 'wll_at'
    )
  }

  /**
   * Check if this is a dimension/length specification
   */
  isDimensionSpec(): boolean {
    const lowerKey = this.key.toLowerCase()
    return (
      lowerKey.includes('dim') ||
      lowerKey.includes('diameter') ||
      lowerKey.includes('length') ||
      lowerKey.includes('height') ||
      lowerKey.includes('width') ||
      lowerKey.includes('depth')
    )
  }

  /**
   * Check if this is a reference that needs resolution
   */
  isUnresolvedReference(): boolean {
    // Check if value looks like a UUID
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(this.value)
    return isUUID && !!this.referenceTable
  }

  /**
   * Get importance score for sorting
   */
  getImportanceScore(): number {
    const lowerKey = this.key.toLowerCase()

    // Critical identification
    if (lowerKey.includes('serial') || lowerKey.includes('asset')) return 1
    if (lowerKey.includes('part') && lowerKey.includes('no')) return 2

    // Weight specifications (in order: Tare, MGW, Capacity)
    if (lowerKey.includes('tare')) return 3
    if (lowerKey === 'mgw') return 4
    if (lowerKey.includes('mgw') && lowerKey.includes('at')) return 5
    if (lowerKey === 'capacity') return 6
    if (lowerKey.includes('payload')) return 7
    if (lowerKey === 'wll' || lowerKey === 'swl') return 8
    if (lowerKey === 'wll_at') return 9

    // Manufacturing
    if (lowerKey.includes('manufacturer') || lowerKey.includes('produsent')) return 10
    if (lowerKey.includes('prod') && lowerKey.includes('year')) return 11
    if (lowerKey.includes('prod') && lowerKey.includes('acc')) return 12
    if (lowerKey.includes('standard')) return 12

    // Dimensions
    if (lowerKey.includes('material')) return 14
    if (this.isDimensionSpec()) return 15

    // Documentation
    if (lowerKey.includes('certificate') || lowerKey.includes('cert')) return 16
    if (lowerKey.includes('ndt')) return 17

    // Everything else gets low priority
    return 100
  }

  /**
   * Create a copy with a new value (for formatting)
   */
  withValue(newValue: string): DynamicSpecification {
    return new DynamicSpecification(
      this.key,
      newValue,
      this.label,
      this.dataType,
      this.unit,
      this.priority,
      this.referenceTable
    )
  }

  /**
   * Create a formatted display string (label + value with line break)
   */
  toDisplayString(formattedValue: string): string {
    return `${this.label}\n${formattedValue}`
  }

  /**
   * Debug string representation
   */
  toString(): string {
    return `DynamicSpecification(key="${this.key}", label="${this.label}", value="${this.value}", unit="${this.unit || 'none'}", priority=${this.priority ?? 'auto'})`
  }
}
